import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'markdown-to-jsx';
import BEMHelper from '@ynap/bem-helper';
import { SizeChart } from '@ynap/api-utils';
import { toSizeConversion } from './SizeChartMapper';
import { useAppState, useMessages, useConfig } from '@ynap/immutables-provider';
import Skeleton from '@ynap/skeleton';

const bem = new BEMHelper('SizeConversionsTable');

const configSelector = (config) => ({
    baseUrl: config?.global.services.commerce.baseUrl,
    clientId: config?.global.services.commerce.clientId,
    brandId: config?.global.application.brandId,
});

const SizeConversionsTable = ({ title, details, onSelectSku, skus, sizeScheme, selectedSku = null }) => {
    const [sizeConversions, setSizeConversions] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [disableLeftArrow, setDisableLeftArrow] = useState(false);
    const [disableRightArrow, setDisableRightArrow] = useState(false);
    const cardListRef = React.useRef();
    const { language, country, langISO } = useAppState();
    const { baseUrl, brandId, clientId } = useConfig(configSelector);
    const messages = useMessages();

    React.useEffect(() => {
        if (!sizeConversions) {
            fetch(
                SizeChart.getSizeChart({
                    baseUrl: baseUrl,
                    sizeScheme: sizeScheme,
                    brandId: brandId,
                    country: country,
                    locale: langISO,
                    clientId: clientId,
                }),
            )
                .then((res) => res.json())
                .then((data) => setSizeConversions(toSizeConversion(data, skus)))
                .finally(() => setIsLoading(false));
        }
    }, [sizeConversions, skus]);
    const getfirstStockedItemIndex = React.useCallback(() => {
        return sizeConversions.findIndex((size) => size.PIDSize?.sizeLabel === skus[0].size.labelSize);
    }, [sizeConversions, skus]);
    React.useEffect(() => {
        if (sizeConversions?.length > 0 && !isLoading && cardListRef?.current) {
            const firstStockedItemIndex = getfirstStockedItemIndex() - 1;
            const scrolldistance = firstStockedItemIndex * 180 + 16 * firstStockedItemIndex;
            cardListRef.current.scrollTo(language === 'ar' ? scrolldistance * -1 : scrolldistance, 0);
        }
    }, [sizeConversions, isLoading]);

    const selectedSkuIndex = React.useMemo(() => {
        return sizeConversions?.findIndex((size) => size?.centralSize?.sizeLabel === selectedSku) || 0;
    }, [sizeConversions, selectedSku]);

    const stockLevel = React.useMemo(() => {
        const fullSku = skus.find((sku) => sku.size.labelSize === selectedSku);
        if (!fullSku) {
            return undefined;
        }
        return fullSku && !fullSku.buyable
            ? messages.sizes.sizeGuide.outOfStock
            : fullSku.oneLeftOnline
              ? messages.sizes.sizeGuide.oneLeft
              : fullSku.lowStockOnline
                ? messages.sizes.sizeGuide.lowStock
                : messages.sizes.sizeGuide.inStock;
    }, [skus, selectedSku, messages]);

    const sizeCardHeaderText = React.useCallback((pidSize, preferredSize, isSelected = false) => {
        if (pidSize && preferredSize) {
            return messages.sizes.sizeConversionModal({
                sizeLabel: `${pidSize.schemaLabel} ${pidSize.sizeLabel}`,
                preferredSize,
                isSelected,
            });
        } else if (pidSize && !preferredSize) {
            return pidSize.sizeLabel.includes(' ') ? `${pidSize.sizeLabel}` : `${pidSize.schemaLabel} ${pidSize.sizeLabel}`;
        } else if (!pidSize && preferredSize) {
            return preferredSize;
        } else {
            return null;
        }
    }, []);

    const onSizeCardsScroll = React.useCallback(() => {
        setTimeout(() => {
            if (cardListRef?.current?.scrollLeft === 0) {
                setDisableLeftArrow(true);
            }
            if (disableLeftArrow && cardListRef?.current?.scrollLeft > 0) {
                setDisableLeftArrow(false);
            }
            if (cardListRef?.current?.scrollLeft + cardListRef?.current?.offsetWidth === cardListRef?.current?.scrollWidth) {
                setDisableRightArrow(true);
            }
            if (disableRightArrow && cardListRef?.current?.scrollLeft + cardListRef?.current?.offsetWidth !== cardListRef?.current?.scrollWidth) {
                setDisableRightArrow(false);
            }
        }, 500);
    }, [disableLeftArrow, disableRightArrow]);

    return (
        <div className={bem('table')}>
            <h2 className={bem('sizeConversionsTitle')}>{title()}</h2>
            <div className={bem('headerContainer', stockLevel ? 'withStock' : null)}>
                <p className={bem('details')}>{details()}</p>
                <div className={bem('controls', language === 'ar' ? 'rtl' : null)}>
                    <div
                        className={bem('leftArrow', disableLeftArrow ? 'disableLeftArrow' : null)}
                        onClick={() => {
                            cardListRef?.current?.scrollBy({
                                top: 0,
                                left: -1 * cardListRef?.current?.offsetWidth - 100,
                                behavior: 'smooth',
                            });
                        }}
                    ></div>
                    <div
                        className={bem('rightArrow', disableRightArrow ? 'disableRightArrow' : null)}
                        onClick={() => {
                            cardListRef?.current?.scrollBy({
                                top: 0,
                                left: cardListRef?.current?.offsetWidth - 100,
                                behavior: 'smooth',
                            });
                        }}
                    ></div>
                </div>
            </div>
            {stockLevel && <p className={bem('stockLevel')}>{stockLevel()}</p>}
            {!isLoading ? (
                sizeConversions?.some((s) => s.sizes.length > 0) ? (
                    <ul className={bem('sizeCards')} ref={cardListRef} onScroll={onSizeCardsScroll}>
                        {sizeConversions.map((size, index) => {
                            const columnModifier = index === selectedSkuIndex ? 'columnHighlight' : null;
                            const isSelectable = skus.find((sku) => sku.size.labelSize === size?.centralSize?.sizeLabel);
                            const notSelectableModifier = !isSelectable ? 'notSelectable' : null;

                            if (size.sizes.length > 0) {
                                return (
                                    <div
                                        className={bem('sizeCard', [columnModifier, notSelectableModifier])}
                                        key={index}
                                        onClick={isSelectable ? () => onSelectSku(size?.centralSize?.sizeLabel) : () => {}}
                                    >
                                        <div className={bem('sizeCardHeaderContainer')}>
                                            <div className={bem('sizeCardHeader')}>
                                                <div className={bem('radioButton', ['sameMargin', notSelectableModifier], null)}>
                                                    <div className={bem('radioButtonInner', columnModifier)}></div>
                                                </div>
                                                <ReactMarkdown
                                                    className={bem('pidSizeLabel')}
                                                    options={{
                                                        forceBlock: true,
                                                        forceWrapper: true,
                                                        wrapper: 'div',
                                                    }}
                                                >
                                                    {sizeCardHeaderText(size?.PIDSize, size?.preferredSize, index === selectedSkuIndex) ||
                                                        messages?.sizes?.sizeGuide?.unavailable?.() ||
                                                        ''}
                                                </ReactMarkdown>
                                            </div>
                                        </div>
                                        <div className={bem('conversionsList')}>
                                            {size.sizes.map((s) => {
                                                return (
                                                    <div className={bem('sizeLabelWrapper')} key={s.schemaLabel}>
                                                        <div className={bem('sizeLabel')}>{s.schemaLabel}</div>
                                                        <div className={bem('sizeLabel', 'bold')}>{s.sizeLabel}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            }
                        })}
                    </ul>
                ) : (
                    <div className={bem('noInfo')}>{messages.sizes.sizeGuide.noFurtherInfo}</div>
                )
            ) : (
                <div className={bem('loading')}>
                    <Skeleton count={1} align={'center'} additionalStyle={{ height: '100px' }} />
                </div>
            )}
        </div>
    );
};

SizeConversionsTable.propTypes = {
    sizeData: PropTypes.array(),
    title: PropTypes.string,
    details: PropTypes.string,
    sizeWrapperClass: PropTypes.string,
    onSelectSku: () => {},
    skus: PropTypes.array,
    selectedSku: PropTypes.string,
    sizeScheme: PropTypes.string,
};

export default SizeConversionsTable;
